import { useState, useEffect, useCallback, useRef } from 'react';
import dataService from '../services/dataService';

// Hook for apps data
export const useApps = (initialParams = {}) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const paramsRef = useRef(initialParams);

  const fetchApps = useCallback(async (overrideParams) => {
    if (overrideParams) paramsRef.current = overrideParams;
    const effectiveParams = paramsRef.current;

    setLoading(true);
    setError(null);
    try {
      const data = await dataService.getApps(effectiveParams);
      setApps(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Do not auto-fetch on mount; caller controls when to fetch via refetch

  return { apps, loading, error, refetch: fetchApps };
};

// Hook for all apps data
export const useAllApps = () => {
  const [allApps, setAllApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAllApps = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await dataService.getAllApps();
      setAllApps(data || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching all apps:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllApps();
  }, [fetchAllApps]);

  return { allApps, loading, error, refetch: fetchAllApps };
};

// Hook for countries data
export const useCountries = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCountries = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await dataService.getCountries();
      setCountries(data || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching countries:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  return { countries, loading, error, refetch: fetchCountries };
};

// Hook for ad networks data
export const useAdNetworks = () => {
  const [adNetworks, setAdNetworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAdNetworks = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await dataService.getAdNetworks();
      setAdNetworks(data || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching ad networks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdNetworks();
  }, [fetchAdNetworks]);

  return { adNetworks, loading, error, refetch: fetchAdNetworks };
};

// Hook for menu data
export const useMenu = () => {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMenu = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await dataService.getMenuList();
      setMenu(data || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching menu:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check cache first
    const cachedMenu = dataService.getCachedData('menu');
    if (cachedMenu) {
      setMenu(cachedMenu);
      return;
    }
    
    // If no cache, fetch
    fetchMenu();
  }, [fetchMenu]);

  return { menu, loading, error, refetch: fetchMenu };
};

// Hook to get cached data without loading state
export const useCachedData = () => {
  const getCachedApps = useCallback(() => dataService.getCachedData('apps'), []);
  const getCachedAllApps = useCallback(() => dataService.getCachedData('allApps'), []);
  const getCachedCountries = useCallback(() => dataService.getCachedData('countries'), []);
  const getCachedAdNetworks = useCallback(() => dataService.getCachedData('adNetworks'), []);
  const getCachedMenu = useCallback(() => dataService.getCachedData('menu'), []);

  return {
    getCachedApps,
    getCachedAllApps,
    getCachedCountries,
    getCachedAdNetworks,
    getCachedMenu
  };
};
